import React, { useState, useEffect } from 'react';
import { FormPreview } from '../components/FormPreview';
import type { CrudFormProps, FormDefinition, WorkflowResult } from './types';

function LoadingSpinner({ text }: { text?: string }) {
	return (
		<div style={{
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			gap: '12px',
			padding: '40px'
		}}>
			<div style={{
				width: '32px',
				height: '32px',
				border: '3px solid rgba(255,255,255,0.1)',
				borderTop: '3px solid rgba(59,130,246,0.95)',
				borderRadius: '50%',
				animation: 'spin 0.8s linear infinite'
			}} />
			{text && (
				<span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.65)' }}>
					{text}
				</span>
			)}
			<style>{`
				@keyframes spin {
					0% { transform: rotate(0deg); }
					100% { transform: rotate(360deg); }
				}
			`}</style>
		</div>
	);
}

export function CrudForm({
	sdk,
	workflowName,
	initialData = {},
	onSuccess,
	onCancel,
	onError
}: CrudFormProps) {
	const [formState, setFormState] = useState<{
		form: FormDefinition | null;
		taskId: string | null;
		workflowId: string | null;
		data: Record<string, any>;
	}>({
		form: null,
		taskId: null,
		workflowId: null,
		data: initialData
	});
	
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	
	// Initialize workflow on mount to get the form
	useEffect(() => {
		const initializeWorkflow = async () => {
			setLoading(true);
			try {
				const result = await sdk.crud.executeWorkflow(workflowName, initialData);
				
				if (result?.result?.form) {
					setFormState({
						form: result.result.form.schema as FormDefinition,
						taskId: result.result.workflow?.current_task?.id || null,
						workflowId: result.result.workflow?.id || null,
						data: result.result.form.initial_data || initialData
					});
				} else if (result?.status === 'COMPLETED') {
					onSuccess?.(result?.result);
				} else if (result?.status === 'ERROR') {
					setError(result?.message || 'Workflow failed');
				}
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to initialize workflow');
			} finally {
				setLoading(false);
			}
		};
		
		initializeWorkflow();
	}, []);  // Run once on mount
	
	const handleSubmit = async (data: Record<string, any>) => {
		setLoading(true);
		setError(null);
		
		try {
			let response: WorkflowResult;
			
			if (formState.taskId) {
				const stateVersion = formState.workflowId 
					? await sdk.crud.getWorkflowStatus(formState.workflowId)
						.then((status: any) => status?.result?.workflow?.current_task?.state_version || 1)
						.catch(() => 1)
					: undefined;
				
				response = await sdk.crud.resumeWorkflow(
					formState.taskId,
					data,
					stateVersion
				);
			} else {
				response = await sdk.crud.executeWorkflow(workflowName, data);
			}
			
			const status = response?.status;
			const workflowResult = response?.result;
			
			if (status === 'COMPLETED') {
				onSuccess?.(workflowResult);
				return;
			}
			
			if (status === 'ERROR') {
				const errorMsg = response?.message || 'Workflow execution failed';
				setError(errorMsg);
				onError?.(new Error(errorMsg));
				return;
			}
			
			if (workflowResult?.form) {
				setFormState({
					form: workflowResult.form.schema as FormDefinition,
					taskId: workflowResult.workflow?.current_task?.id || null,
					workflowId: workflowResult.workflow?.id || formState.workflowId,
					data: workflowResult.form.initial_data || {}
				});
			} else {
				onSuccess?.(workflowResult);
			}
		} catch (err) {
			const errorMsg = err instanceof Error ? err.message : 'Workflow execution failed';
			setError(errorMsg);
			onError?.(err instanceof Error ? err : new Error(errorMsg));
		} finally {
			setLoading(false);
		}
	};
	
	if (loading && !formState.form) {
		return <LoadingSpinner text="Loading form..." />;
	}
	
	if (error) {
		return (
			<div style={{ 
				padding: '20px', 
				background: 'var(--error-bg)',
				border: '1px solid var(--error-border)',
				borderRadius: '8px',
				color: 'var(--error-color)'
			}}>
				⚠️ {error}
			</div>
		);
	}
	
	if (!formState.form) {
		return <LoadingSpinner text="Initializing form..." />;
	}
	
	return (
		<FormPreview
			form={formState.form}
			initialValues={formState.data}
			onSubmit={handleSubmit}
			onCancel={onCancel}
		/>
	);
}

